import log from 'loglevel';

const gallerySelector = "[data-solution-gallery-trigger]";

export function initSolutionGallery() {
  const triggers = Array.from(document.querySelectorAll(gallerySelector));

  if (triggers.length === 0) { return; }

  const items = triggers.map((trigger) => ({
    src: trigger.dataset.gallerySrc,
    title: trigger.dataset.galleryTitle || "",
    description: trigger.dataset.galleryDescription || "",
  })).filter((item) => item.src);

  if (items.length === 0) { return; }

  const gallery = buildGallery();
  document.body.appendChild(gallery.element);

  let currentIndex = 0;
  let previousFocus = null;

  const open = (index) => {
    currentIndex = index;
    previousFocus = document.activeElement;
    updateGallery(gallery, items, currentIndex);
    gallery.element.hidden = false;
    document.body.classList.add("offer-gallery-open");
    window.requestAnimationFrame(() => gallery.element.classList.add("offer-image-gallery--open"));
    gallery.element.focus();
    document.addEventListener("keydown", onKeydown);
  };

  const close = () => {
    gallery.element.classList.remove("offer-image-gallery--open");
    gallery.element.hidden = true;
    document.body.classList.remove("offer-gallery-open");
    document.removeEventListener("keydown", onKeydown);
    previousFocus?.focus();
  };

  const showNext = () => {
    currentIndex = (currentIndex + 1) % items.length;
    updateGallery(gallery, items, currentIndex);
  };

  const showPrevious = () => {
    currentIndex = (currentIndex - 1 + items.length) % items.length;
    updateGallery(gallery, items, currentIndex);
  };

  const onKeydown = (event) => {
    if (event.key === "Escape") {
      close();
    } else if (event.key === "ArrowRight") {
      showNext();
    } else if (event.key === "ArrowLeft") {
      showPrevious();
    } else if (event.key === "Tab") {
      trapFocus(event, gallery.element);
    }
  };

  triggers.forEach((trigger, index) => {
    trigger.addEventListener("click", () => open(index));
  });

  gallery.closeButton.addEventListener("click", close);
  gallery.nextButton.addEventListener("click", showNext);
  gallery.previousButton.addEventListener("click", showPrevious);
  gallery.element.addEventListener("click", (event) => {
    if (event.target === gallery.element) close();
  });

  log.info("Solution gallery loaded");
}

function buildGallery() {
  const element = document.createElement("div");
  element.className = "offer-image-gallery";
  element.hidden = true;
  element.setAttribute("role", "dialog");
  element.setAttribute("aria-modal", "true");
  element.setAttribute("aria-label", "Solution image viewer");
  element.setAttribute("tabindex", "-1");

  element.innerHTML = `
    <div class="offer-image-gallery__stage">
      <button class="offer-image-gallery__close" type="button" aria-label="Close image viewer">&times;</button>
      <button class="offer-image-gallery__nav offer-image-gallery__nav--previous" type="button" aria-label="Previous image">&#8592;</button>
      <figure class="offer-image-gallery__figure">
        <img class="offer-image-gallery__image" alt="" />
        <figcaption class="offer-image-gallery__caption">
          <span class="offer-image-gallery__title"></span>
          <span class="offer-image-gallery__description"></span>
          <span class="offer-image-gallery__counter"></span>
        </figcaption>
      </figure>
      <button class="offer-image-gallery__nav offer-image-gallery__nav--next" type="button" aria-label="Next image">&#8594;</button>
    </div>
  `;

  return {
    element,
    image: element.querySelector(".offer-image-gallery__image"),
    title: element.querySelector(".offer-image-gallery__title"),
    description: element.querySelector(".offer-image-gallery__description"),
    counter: element.querySelector(".offer-image-gallery__counter"),
    closeButton: element.querySelector(".offer-image-gallery__close"),
    previousButton: element.querySelector(".offer-image-gallery__nav--previous"),
    nextButton: element.querySelector(".offer-image-gallery__nav--next"),
  };
}

function updateGallery(gallery, items, currentIndex) {
  const item = items[currentIndex];

  gallery.image.src = item.src;
  gallery.image.alt = item.description;
  gallery.title.textContent = item.title;
  gallery.description.textContent = item.description;
  gallery.counter.textContent = `${currentIndex + 1} / ${items.length}`;
}

function trapFocus(event, element) {
  const focusable = Array.from(element.querySelectorAll("button:not([disabled])"));
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (!first || !last) { return; }

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
